import '../../../src/index.css';
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router';
import { IoEye, IoEyeOff, IoSchoolOutline, IoPersonOutline, IoLockClosedOutline } from "react-icons/io5";
import { useAuth } from '../../auth/AuthenticationWrapper';
import logo from '../../resources/logo.png';
import CustomButton from '../../components/lib/customButton';
import LoginBackground from '../../resources/bg_new.png';
import LoginBackgroundMobile from '../../resources/bg_new_mobile.png';

function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [userCredentials, setUserCredentials] = useState({
        login: '',
        password: '',
        login_code: '',
    });

    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [rememberMe, setRememberMe] = useState(false);

    useEffect(() => {
        const savedLoginCode = localStorage.getItem('rememberedLoginCode');
        const savedLogin = localStorage.getItem('rememberedLogin');
        if (savedLoginCode || savedLogin) {
            setUserCredentials((prev) => ({
                ...prev,
                login_code: savedLoginCode || '',
                login: savedLogin || ''
            }));
            setRememberMe(true);
        }
    }, []);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setUserCredentials((prev) => ({
            ...prev,
            [name]: value
        }));
        if (error) setError(null);
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!userCredentials.login || !userCredentials.password) {
            setError('Preencha todos os campos obrigatórios.');
            return;
        }

        try {
            const result = await login(userCredentials);

            if (result && result.user && result.token) {
                const { user } = result;
                const role = user.role;
                const userLogin = user.login;

                if (rememberMe) {
                    localStorage.setItem('rememberedLoginCode', userCredentials.login_code);
                    localStorage.setItem('rememberedLogin', userCredentials.login);
                } else {
                    localStorage.removeItem('rememberedLoginCode');
                    localStorage.removeItem('rememberedLogin');
                }

                setTimeout(() => {
                    const routes = {
                        "ADMIN": '/financeiro',
                        "CASHIER": '/vendas',
                        "ATTENDANT": '/pedidos',
                        "STUDENT": '/novacompra',
                        "PARENT": `/clientes/detalhesResponsavel/${userLogin}`
                    };
                    navigate(routes[role] || '/');
                }, 1000);
            } else {
                setError('Credenciais inválidas. Verifique seus dados.');
            }
        } catch (error) {
            console.error('Error during login:', error);
            if (error.response && error.response.status === 401) {
                setError('Credenciais inválidas. Verifique seus dados.');
            }
            else if (error.status === 401) {
                setError('Credenciais inválidas. Verifique seus dados.');
            }
            else {
                setError('Falha na conexão. Tente novamente mais tarde.');
            }
        }
    };

    const inputContainerStyle = "relative flex items-center mb-4";
    const iconStyle = "absolute left-3 text-gray-400 text-xl";
    const inputStyle = "w-full pl-10 pr-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 placeholder-gray-400";

    return (
        <div className='relative h-screen w-full overflow-hidden font-sans'>
            <div className="absolute inset-0 z-0">
                <img
                    src={LoginBackgroundMobile}
                    alt='cafeteria vertical background'
                    className='absolute inset-0 object-cover w-full h-full blur-[3px] scale-105 block md:hidden'
                />

                <img
                    src={LoginBackground}
                    alt='cafeteria landscape background'
                    className='absolute inset-0 object-cover w-full h-full blur-[3px] scale-105 hidden md:block'
                />

                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/60"></div>
            </div>

            <div className='flex items-center justify-center h-full relative z-10 p-4'>
                <div className='w-full max-w-md bg-white/95 sm:bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 -mt-10 sm:p-10 border border-white/20 transform transition-all hover:shadow-xl'>

                    <div className="flex flex-col items-center mb-8">
                        <img
                            src={logo}
                            alt='Logo Foodz'
                            className='w-48 h-auto object-contain mb-2 drop-shadow-sm'
                        />
                    </div>

                    <form onSubmit={handleSubmit} className='flex flex-col' method="post">

                        <div className={inputContainerStyle}>
                            <IoSchoolOutline className={iconStyle} />
                            <input
                                className={inputStyle}
                                name="login_code"
                                type="text"
                                placeholder='Código da Escola'
                                value={userCredentials.login_code}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className={inputContainerStyle}>
                            <IoPersonOutline className={iconStyle} />
                            <input
                                className={inputStyle}
                                name="login"
                                type="text"
                                placeholder='Usuário'
                                autoComplete="username"
                                value={userCredentials.login}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="relative flex items-center mb-2">
                            <IoLockClosedOutline className={iconStyle} />
                            <input
                                className={inputStyle}
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder='Senha'
                                autoComplete="current-password"
                                value={userCredentials.password}
                                onChange={handleInputChange}
                            />
                            <button
                                type="button"
                                onClick={handleTogglePasswordVisibility}
                                className="absolute right-3 text-gray-400 hover:text-blue-600 focus:outline-none transition-colors p-1"
                            >
                                {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
                            </button>
                        </div>

                        <div className="flex items-center justify-between mt-2 mb-6">
                            <label className="flex items-center cursor-pointer group">
                                <div className="relative flex items-center">
                                    <input
                                        type="checkbox"
                                        className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-gray-300 shadow-sm checked:bg-blue-600 checked:border-blue-600 transition-all"
                                        checked={rememberMe}
                                        onChange={() => setRememberMe(!rememberMe)}
                                    />
                                    <svg className="absolute w-3 h-3 text-white hidden peer-checked:block left-0.5 pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </div>
                                <span className="ml-2 text-sm text-gray-500 group-hover:text-gray-700 transition-colors">Lembrar de mim</span>
                            </label>
                        </div>

                        <CustomButton
                            buttonType={'confirm'}
                            type={'submit'}
                            text={'ENTRAR'}
                            showIcon={false}
                            className={'w-full py-3.5 rounded-xl shadow-lg shadow-blue-500/30 font-bold tracking-wide transform active:scale-95 transition-all'}
                        />

                        {error && (
                            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg animate-pulse">
                                <p className="text-center text-red-500 text-sm font-medium flex items-center justify-center gap-2">
                                    ⚠️ {error}
                                </p>
                            </div>
                        )}
                    </form>
                </div>

                <div className="absolute bottom-4 text-white/60 text-xs font-light tracking-wider">
                    &copy; {new Date().getFullYear()} Foodz System. Todos os direitos reservados.
                </div>
            </div>
        </div>
    );
}

export default Login;